import {
  ConversionCopy,
  FeaturedProductProps,
} from "@bluebottlecoffee/design-system/components";
import { QuantityGuide } from "../sanity-schema";
import { DereferencedFeaturedProduct } from "../sanity/subscription-queries";
import { toProductProps } from "./conversion";
import { toQuantityGuideProps } from "./quantity-guide";

/** Copy shared with the PDP {@link Conversion} module (translation group). */
type FeaturedProductCopySource = Pick<
  ConversionCopy,
  | "subscribe"
  | "schedule"
  | "sizeSelectorLabel"
  | "soldOut"
  | "addToCart"
  | "optionAvailable"
  | "quantityGuide"
>;

/** The props resolved at build time. The interactive handlers
 * (subscribeProduct, addToCart, useVariantPrice, useVariantAvailability,
 * clickTracking) are supplied client-side by the wrapper. */
export type StaticFeaturedProductProps = Omit<
  FeaturedProductProps,
  | "subscribeProduct"
  | "addToCart"
  | "addToCartProps"
  | "useVariantPrice"
  | "useVariantAvailability"
  | "clickTracking"
>;

export function toFeaturedProductProps(
  data: DereferencedFeaturedProduct,
  copy: FeaturedProductCopySource,
  quantityGuide: QuantityGuide,
  lang: string,
): StaticFeaturedProductProps {
  const { product, featuredBadge, review, showQuantityGuide } = data;

  return {
    product: toProductProps(product, lang),
    copy,
    featuredBadge: {
      label: featuredBadge?.label?.[lang] ?? "",
      pauseAriaLabel: featuredBadge?.pauseAriaLabel?.[lang] ?? "",
      playAriaLabel: featuredBadge?.playAriaLabel?.[lang] ?? "",
    },
    showQuantityGuide: showQuantityGuide ?? false,
    ...(review?.quote &&
      review?.author && {
        review: {
          quote: review.quote[lang],
          author: review.author[lang],
          ...(review.source && { source: review.source[lang] }),
        },
      }),
    ...(quantityGuide && {
      quantityGuide: toQuantityGuideProps(quantityGuide, lang),
    }),
  };
}
